<template>
    <div class="tabs">
        <div class="tabs_header">
            <div class="tabs_nav">
                <button
                    v-for="(tab, index) in tabs"
                    :key="tab.id"
                    :class="[
                        'tabs_button',
                        { 'tabs_button--active': activeTab === tab.id }
                    ]"
                    @click="setActiveTab(tab.id)"
                >
                    <div class="tabs_buttonIcon" v-if="tab.icon">
                        <sw-icon :name="tab.icon" />
                    </div>
                    <span class="tabs_buttonText">{{ tab.label }}</span>
                    <div class="tabs_buttonBadge" v-if="tab.badge">
                        {{ tab.badge }}
                    </div>
                </button>
            </div>
        </div>

        <div class="tabs_content">
            <div class="tabs_panel">
                <slot :name="activeTab" :activeTab="activeTab"></slot>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';

const props = defineProps({
    tabs: {
        type: Array,
        required: true,
        // Exemplo: [{ id: 'general', label: 'Geral', icon: 'settings' }]
    },
    defaultTab: {
        type: String,
        default: ''
    }
});

const emit = defineEmits(['tab-change']);

const activeTab = ref(props.defaultTab || props.tabs[0]?.id);
const setActiveTab = (tabId) => {
    activeTab.value = tabId;
    emit('tab-change', tabId);
};
</script>

<style lang="scss" scoped>
@import '@/assets/css/mediaScreen.scss';

.tabs {
    width: 100%;
}

.tabs_header {
    position: relative;
    margin-bottom: 2em;
    border-bottom: 1px solid var(--borderColorLight, #e5e7eb);
}

.tabs_nav {
    display: flex;
    position: relative;
    gap: 0.5em;
}

.tabs_button {
    display: flex;
    align-items: center;
    gap: 0.75em;
    padding: 1em 1.5em;
    background: none;
    border: none;
    border-radius: 0.75em 0.75em 0 0;
    cursor: pointer;
    font-size: 0.875em;
    font-weight: 600;
    color: var(--textSecondary, #64748b);
    position: relative;
    min-height: 3.5em;

    &:hover {
        background: rgba(59, 130, 246, 0.05);
        color: var(--colorBlue, #3b82f6);
    }

    &--active {
        color: var(--colorBlue, #3b82f6);
        background: rgba(59, 130, 246, 0.08);
        border-bottom: 3px solid var(--colorBlue, #3b82f6);

        .tabs_buttonIcon {
            background: linear-gradient(135deg, var(--colorBlue, #3b82f6), var(--colorBlueDark, #1d4ed8));
            color: white;
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }
    }
}

.tabs_buttonIcon {
    width: 2em;
    height: 2em;
    border-radius: 0.5em;
    background: rgba(107, 114, 128, 0.1);
    color: var(--textSecondary, #6b7280);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1em;
    flex-shrink: 0;
}

.tabs_buttonText {
    font-weight: 600;
    letter-spacing: 0.025em;
}

.tabs_buttonBadge {
    background: linear-gradient(135deg, var(--colorOrange, #f59e0b), var(--colorOrangeDark, #d97706));
    color: white;
    font-size: 0.75em;
    font-weight: 700;
    padding: 0.25em 0.5em;
    border-radius: 0.375em;
    min-width: 1.5em;
    text-align: center;
    box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
}


.tabs_content {
    width: 100%;
}

.tabs_panel {
    animation: fadeInUp 0.4s ease-out;
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(0.5em);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Responsividade */
@include screen_medias(s, m) {
    .tabs_nav {
        overflow-x: auto;
        overflow-y: hidden;
        scroll-behavior: smooth;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        -ms-overflow-style: none;

        &::-webkit-scrollbar {
            display: none;
        }
    }

    .tabs_button {
        padding: 0.875em 1.25em;
        gap: 0.5em;
        min-width: auto;
        flex-shrink: 0;
    }

    .tabs_buttonIcon {
        width: 1.75em;
        height: 1.75em;
        font-size: 0.875em;
    }

    .tabs_buttonText {
        font-size: 0.875em;
    }
}

@include screen_s {
    .tabs_button {
        padding: 1em 1.25em;
        flex-direction: column;
        gap: 0.5em;
        text-align: center;
        min-width: 6em;
        min-height: 4em;

    }

    .tabs_buttonIcon {
        width: 1.75em;
        height: 1.75em;
        font-size: 0.875em;
    }

    .tabs_buttonText {
        font-size: 0.8125em;
        line-height: 1.3;
        font-weight: 600;
    }

    .tabs_buttonBadge {
        position: absolute;
        top: 0.375em;
        right: 0.375em;
        font-size: 0.6875em;
        min-width: 1.375em;
    }

}
</style>